import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import css from './toolbar.module.css';
import { useService } from '../../../../../services/services';
import DateFilter, {
  DateFields,
} from '../../../../../components/common/table/date-filter/date-filter';
import ExcelButtonNew from '../../../../../components/common/table/excel-button/excel-button-new';
import ValueFilter from '../../../../../components/common/table/value-filter/value-filter';
import ShowFilter from '../../../../../components/common/table/show-filter/show-filter';
import { useQuery } from '../../../../../hooks/use-query';
import { ButtonComponent } from '../../../../../components/form/elements/button';
import { Option } from '../../../../../components/form/elements/select';
import { MultiValue } from 'react-select';
const DEBOUNCE_TIME = 3000;

interface Props {
  useCalendarView: boolean;
  setUseCalendarView: Dispatch<SetStateAction<boolean>>;
  eventsCount: number;
  hasAnimalsTable: boolean;
}
export default function Toolbar({
  useCalendarView,
  setUseCalendarView,
  eventsCount = 1,
  hasAnimalsTable = true,
}: Props) {
  const { t } = useTranslation();
  const transKeyPrefix = 'pages.livestock.animals_and_events.events.';
  const tp = useCallback((key: string) => t(transKeyPrefix.concat(key)), [t]);
  const { farmId } = useParams<{ farmId: string }>();
  const { animalEventService } = useService();
  const [species, setSpecies] = useState([]);
  const [barns, setBarns] = useState([]);
  const [events, setEvents] = useState([]);
  const [subEvents, setSubEvents] = useState([]);
  const [showFilter, setShowFilter] = useState(true);
  const [showAnimalEidFilter, setShowAnimalEidFilter] = useState(false);
  const [animalEids, setAnimalEids] = useState([]);
  const [params] = useQuery();

  const individualAnimals = useMemo(
    () => [
      t('pages.livestock.animals_and_events.filters.species.cattle'),
      t('pages.livestock.animals_and_events.filters.species.sheep'),
      t('pages.livestock.animals_and_events.filters.species.goats'),
      t('pages.livestock.animals_and_events.filters.species.horses'),
    ],
    [t]
  );

  useEffect(() => {
    const customParams = params ? { ...params, species: '' } : {};
    const species = animalEventService
      .species(farmId, customParams)
      .subscribe((species) => {
        if (!params?.species) {
          setShowAnimalEidFilter(individualAnimals.includes(species[0]?.label));
        }
        setSpecies(species.sort((a, b) => a.label.localeCompare(b.label)));
      });

    const barns = animalEventService
      .barns(farmId, {
        animal_group: hasAnimalsTable ? 0 : 1
      })
      .subscribe((barns) => setBarns(barns));

    const { event, ...eventParams } = params;
    const events = animalEventService
      .events(farmId, eventParams)
      .subscribe((events) => setEvents(events));

    const { sub_event, ...subEventParams } = params;
    const subEvents = animalEventService
      .subEvents(farmId, subEventParams)
      .subscribe((events) => setSubEvents(events));

    return () => {
      species.unsubscribe();
      barns.unsubscribe();
      subEvents.unsubscribe();
      events.unsubscribe();
    };
  }, [animalEventService, farmId, params, individualAnimals, hasAnimalsTable]);

  useEffect(() => {
    if (showAnimalEidFilter) {
      const { animal_eid, ...animalParams } = params;
      const $animalEids = animalEventService
        .animalEids(farmId, animalParams)
        .subscribe((animalEids) => setAnimalEids(animalEids));

      return () => {
        $animalEids.unsubscribe();
      };
    }
  }, [animalEventService, farmId, showAnimalEidFilter, params]);

  const onSpeciesChange = (
    search: URLSearchParams,
    name: string,
    options: Option | MultiValue<Option>
  ) => {
    search.delete(name);
    if (options) {
      const species = options as unknown as Option[];
      let newSelectedOptions = [];

      if (
        species.length > 1 &&
        individualAnimals.includes(species[0].label) !==
          individualAnimals.includes(species[species.length - 1].label)
      ) {
        newSelectedOptions[0] = species[species.length - 1];
      } else {
        newSelectedOptions = species;
      }

      newSelectedOptions.forEach((option) => search.append(name, option.value));
    }
  };

  return (
    <div className={css['animal-list-topbar']}>
      <div className={css['filter-bar']}>
        <div className={css['filter-show']}>
          <label htmlFor="show-filter" className={css['filter-label']}></label>
          <ShowFilter
            onClick={() => setShowFilter(!showFilter)}
            defaultActiveValue={true}
          />
        </div>

        <div className={css['excel-export']}>
          <label htmlFor="excel-export" className={css['filter-label']}></label>
          <ExcelButtonNew
            onClick={() => {
              return animalEventService
                .exportXls(farmId, { ...params })
                .subscribe();
            }}
          />
        </div>
        <div className={css['change-view']}>
          <ButtonComponent
            name="change-view"
            label={t(
              'pages.livestock.animals_and_events.events.calendar_of_events'
            )}
            click={() => {
              setUseCalendarView(!useCalendarView);
            }}
          />
          <span className={css['events-count']}>{eventsCount}</span>
        </div>
      </div>
      {showFilter && (
        <div className={css['filter-bar']}>
          <div className={css['date-from-filter']}>
            <label htmlFor="date_from">{tp('date_from')}</label>
            <DateFilter
              name={'date_from'}
              fields={[DateFields.from]}
              showDateControls={false}
              debounceTime={DEBOUNCE_TIME}
            />
          </div>

          <div className={css['date-to-filter']}>
            <label htmlFor="date_to">{tp('date_to')}</label>
            <DateFilter
              name={'date_to'}
              fields={[DateFields.to]}
              showDateControls={false}
              debounceTime={DEBOUNCE_TIME}
            />
          </div>

          <div className={css['species-filter']}>
            <label htmlFor="species" className={css['filter-label']}>
              {tp('species')}
            </label>
            <div className={css['species-filter-container']}>
              <ValueFilter
                name="species"
                options={species}
                className={'value-filter-width-190'}
                isMulti={true}
                showSelectedCountInsteadOfValue={true}
                closeMenuOnSelect={false}
                onOptionChange={onSpeciesChange}
              />
            </div>
          </div>

          <div className={css['barn-filter']}>
            <label htmlFor="barn_ldc_nr" className={css['filter-label']}>
              {tp('barn')}
            </label>
            <div className={css['barn-filter-container']}>
              <ValueFilter
                name="barn_ldc_nr"
                options={barns}
                className={'value-filter-width-190'}
                isMulti={true}
                showSelectedCountInsteadOfValue={true}
                closeMenuOnSelect={false}
              />
            </div>
          </div>
          <div className={css['event-filter']}>
            <label htmlFor="event" className={css['filter-label']}>
              {tp('event_type')}
            </label>
            <div className={css['event-container']}>
              <ValueFilter
                name="event"
                options={events}
                className={'value-filter-width-190'}
                isMulti={true}
                showSelectedCountInsteadOfValue={true}
                closeMenuOnSelect={false}
              />
            </div>
          </div>
          <div className={css['event-filter']}>
            <label htmlFor="sub_event" className={css['filter-label']}>
              {tp('event_type')}
            </label>
            <div className={css['event-container']}>
              <ValueFilter
                name="sub_event"
                options={subEvents}
                className={'value-filter-width-200'}
                isMulti={true}
                showSelectedCountInsteadOfValue={true}
                closeMenuOnSelect={false}
              />
            </div>
          </div>
          {showAnimalEidFilter && (
            <div className={css['animal-eid-filter']}>
              <label htmlFor="animal_eid" className={css['filter-label']}>
                {tp('animal_id_long')}
              </label>
              <div className={css['animal-eid-filter-container']}>
                <ValueFilter
                  name="animal_eid"
                  options={animalEids}
                  className={'value-filter-width-190'}
                  isMulti={true}
                  showSelectedCountInsteadOfValue={true}
                  closeMenuOnSelect={false}
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
